
/**
 * Module dependencies.
 */

import { Icon } from 'react-components/media';
import { IconButton } from './buttons/icon-button';
import { ReactNode } from 'react';
import { Text } from './typography';
import { color, media } from 'react-components/styles';
import { get, isString } from 'lodash';
import { switchProp, theme } from 'styled-tools';
import { useBreakpoint } from '../breakpoint';
import { useTranslate } from '../i18n';
import closeIcon from './assets/svg/16/close.svg';
import errorIcon from './assets/svg/24/error.svg';
import infoIcon from './assets/svg/24/info.svg';
import styled, { css } from 'styled-components';
import successIcon from './assets/svg/24/success.svg';
import warningIcon from './assets/svg/24/warning.svg';

/**
 * Export `MessageType` type.
 */

export type MessageType = 'error' | 'info' | 'success' | 'warning';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  className?: string;
  onClose?: () => void;
  type: MessageType;
};

/**
 * Icons.
 */

const icons = {
  error: errorIcon,
  info: infoIcon,
  success: successIcon,
  warning: warningIcon
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  border-radius: ${theme('dimensions.borderRadius')}px;
  display: flex;
  min-height: 42px;
  position: relative;

  ${media.min('md')`
    min-height: 52px;
  `}
`;

/**
 * `IconWrapper` styled component.
 */

const IconWrapper = styled.div<{ type: MessageType }>`
  align-items: center;
  border-radius: 4px 0 0 4px;
  display: flex;
  justify-content: center;
  padding: 13px 12px;
  position: relative;

  ${switchProp('type', {
    error: css`
      background-color: ${color('danger')};
    `,
    info: css`
      background-color: ${color('info')};
    `,
    success: css`
      background-color: ${color('success')};
    `,
    warning: css`
      background-color: ${color('warning')};
    `
  })}

  ${media.min('md')`
    padding: 14px 16px;
  `}
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div<{ type: MessageType }>`
  align-items: center;
  border: 1px solid;
  border-bottom-right-radius: ${theme('dimensions.borderRadius')}px;
  border-left: none;
  border-top-right-radius: ${theme('dimensions.borderRadius')}px;
  display: grid;
  flex: 1;
  grid-template-areas: 'text close';
  grid-template-columns: 1fr max-content;
  padding-left: 12px;
  padding-right: 12px;
  position: relative;

  ${switchProp('type', {
    error: css`
      background-color: ${color('danger100')};
      border-color: ${color('danger500')};
      color: ${color('danger500')};
    `,
    info: css`
      background-color: ${color('info100')};
      border-color: ${color('info500')};
      color: ${color('info500')};
    `,
    success: css`
      background-color: ${color('success100')};
      border-color: ${color('success500')};
      color: ${color('success900')};
    `,
    warning: css`
      background-color: ${color('warning100')};
      border-color: ${color('warning500')};
      color: ${color('warning700')};
    `
  })}

  ${media.min('md')`
    padding-left: 16px;
    padding-right: 16px;
  `}
`;

/**
 * `StyledText` styled component.
 */

const StyledText = styled(Text).attrs({ size: 'small' })`
  color: inherit;
  grid-area: text;
  padding: 6px 0;
`;

/**
 * `StyledIconButton` styled component.
 */

const StyledIconButton = styled(IconButton)`
  grid-area: close;
  margin-left: 12px;
  margin-right: -6px;
`;

/**
 * Export `Message` component.
 */

export function Message(props: Props): JSX.Element {
  const { children, className, onClose, type } = props;
  const { translate } = useTranslate();
  const isDesktop = useBreakpoint('md', 'min');

  return (
    <Wrapper className={className}>
      <IconWrapper type={type}>
        <Icon
          aria-hidden
          color={color('white')}
          icon={get(icons, type)}
          size={isDesktop ? '24px' : '16px'}
        />
      </IconWrapper>

      <ContentWrapper type={type}>
        <StyledText {...!isString(children) ? { as: 'div' } : {}}>
          {children}
        </StyledText>

        {!!onClose && (
          <StyledIconButton
            aria-label={translate('common.actions.close')}
            colorTheme={'transparent'}
            icon={closeIcon}
            iconSize={'small'}
            onClick={onClose}
            size={'small'}
          />
        )}
      </ContentWrapper>
    </Wrapper>
  );
}
